<template>
  <b-modal
    id="modal-list-fleet"
    ref="modal-list-fleet"
    size="xl"
    centered
    scrollable
    :title="$t('rjm.listFleetTitle')"
    data-me="rjm-component-modal-list-fleet"
  >
    <McAlert
      v-if="vehicles.some((v) => v.audioIssuanceStatus === 'Failed')"
      variant="warning"
      class="mb-3"
      withIcon
      :text="$t('rjm.voiceAnnouncerFailedInstall')"
    >
      <template #button>
        <McButton
          variant="link"
          :text="$t('rjm.tryAgainAll')"
          icon="icon-outline_refresh"
          data-me="rjm-component-modal-list-fleet-button-try-again-all"
        />
      </template>
    </McAlert>

    <McGrapqlTable
      ref="table"
      :apiClient="query"
      :fields="fields"
      :hasDelete="false"
      :hasCheckbox="false"
      searchable
      :customHandlers="customHandlers"
      data-me="rjm-component-modal-list-fleet-table"
    >
      <template #cell(audio)="{ item }">
        <div class="d-flex align-items-center">
          <span v-if="item.audioIssuanceStatus === 'Failed'" class="text-danger">
            {{ $t('rjm.failed') }}
            <i
              :class="['icon-outline_refresh', isRetrying(item) ? 'opacity-50 pe-none' : '']"
              style="cursor: pointer"
              role="button"
              tabindex="0"
              @click.stop="retry(item)"
              @keydown.enter.stop.prevent="retry(item)"
              data-me="rjm-component-modal-list-fleet-icon-retry"
            />
          </span>
          <span v-else-if="item.audioIssuanceStatus === 'Pending'">
            {{ $t('rjm.inProgress') }}...
          </span>
          <span v-else-if="item.audioIssuanceStatus === 'Success'" class="text-success">
            <i class="icon-fill_checkmark-circle" /> {{ $t('rjm.installed') }}
          </span>
        </div>
      </template>
    </McGrapqlTable>

    <template #modal-footer>
      <McButton
        variant="secondary"
        :text="$t('global.action.close')"
        @click="hide()"
        data-me="rjm-component-modal-list-fleet-button-close"
      />
    </template>
  </b-modal>
</template>

<script>
import McGrapqlTable from '@/components/McGrapqlTable'
import McAlert from '@/components/McAlert'
import McButton from '@/components/McButton'
import { retryAudioIssuance } from '@/services/api/master/rjm'

export default {
  components: {
    McGrapqlTable,
    McAlert,
    McButton,
  },
  data() {
    return {
      customHandlers: {
        handleClickRow: () => {},
        handleAddEmpty: () => {},
      },
      audioFileId: null,
      vehicles: [],
      retryingImeis: {},
    }
  },
  computed: {
    fields() {
      return [
        {
          key: 'licensePlate',
          label: this.$t('global.object.licensePlate'),
          sortable: true,
        },
        {
          key: 'audio',
          label: this.$t('rjm.audio'),
          sortable: false,
        },
        {
          key: 'vehicleGroup',
          label: this.$t('global.object.fleetGroup'),
          sortable: true,
          formatter: (val, key, item) => {
            const groups = (item.vehicle2Vgroup || [])
              .map((v2vg) => v2vg.vgroup?.name)
              .filter(Boolean)
            if (groups.length === 0) return '-'
            if (groups.length > 3) {
              return groups.slice(0, 3).join(', ') + ` +${groups.length - 3}`
            }
            return groups.join(', ')
          },
        },
        {
          key: 'vehicleType',
          label: this.$t('global.object.manufacture'),
          sortable: true,
          formatter: (val, key, item) => {
            if (!item.vehicleType) return '-'
            return `${item.vehicleType.manufacturer} - ${item.vehicleType.model}`
          },
        },
      ]
    },
    query() {
      return {
        get: async (filter) => {
          const pagination = (filter && filter.pagination) || {}
          const page = pagination.page || 1
          const show = pagination.show || 10

          let filteredVehicles = this.vehicles
          if (filter && filter.search) {
            const searchLower = filter.search.toLowerCase()
            filteredVehicles = this.vehicles.filter((v) =>
              (v.licensePlate || '').toLowerCase().includes(searchLower)
            )
          }

          const total = filteredVehicles.length
          const start = (page - 1) * show
          const end = start + show
          const paginatedData = filteredVehicles.slice(start, end)

          return {
            data: paginatedData,
            pagination: {
              total: total,
              page: page,
              show: show,
            },
          }
        },
      }
    },
  },
  methods: {
    show(payload) {
      let vehicles = payload
      let audioFileId = null

      if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
        if (Array.isArray(payload.vehicles)) {
          vehicles = payload.vehicles
        }
        audioFileId = payload.audioFileId || null
      }

      this.audioFileId = audioFileId
      this.vehicles = vehicles || []
      this.$refs['modal-list-fleet'].show()
      this.$nextTick(() => {
        if (this.$refs.table) {
          this.$refs.table.getData({
            page: 1,
            limit: 10,
          })
        }
      })
    },
    isRetrying(item) {
      const imei = item?.imei
      return !!(imei && this.retryingImeis[imei])
    },
    async retry(item) {
      const imei = String(item?.imei || '')
      if (!this.audioFileId || !imei || this.isRetrying(item)) return

      const prevStatus = item.audioIssuanceStatus
      this.$set(this.retryingImeis, imei, true)
      item.audioIssuanceStatus = 'Pending'

      try {
        const res = await this.$apollo.mutate({
          mutation: retryAudioIssuance,
          variables: {
            input: {
              audioFileId: this.audioFileId,
              imei,
            },
          },
        })

        const result = res?.data?.retryAudioIssuance
        if (result && result.isSuccessful === false) {
          throw new Error(result.message)
        }
      } catch (e) {
        item.audioIssuanceStatus = prevStatus
        this.$mcToast.error(e?.message)
      } finally {
        this.$delete(this.retryingImeis, imei)
      }
    },
    hide() {
      this.$refs['modal-list-fleet'].hide()
    },
  },
}
</script>
